/**
 * The Care sections' own vocabulary, mapped onto the panel-wide design tokens
 * in component/common/designSystem/theme.js — the same split cessationTheme.js
 * and gamesTheme.js use: only domain meaning lives here, palettes belong to the
 * design system.
 *
 * ONE FOLDER, TWO SECTIONS. Care Programmes and Reproductive Health are
 * separate routes with separate layouts so a customer can have one without the
 * other, but they are the same shape of page over the same shape of data, so
 * they share these components. The split that matters is in the routing and in
 * the backend's section check, not in duplicated files.
 */
import { CHART_COLORS } from '../../../../component/common/designSystem';

/** The two sections, and the programmes each one owns. */
export const SECTION_PROGRAMMES = {
  care: ['glp-1', 'pause', 'peptide', 'fibromyalgia'],
  reproductive: ['menstrual', 'fertility']
};

export const SECTION_LABELS = {
  care: 'Care Programmes',
  reproductive: 'Reproductive Health'
};

export const PROGRAMME_LABELS = {
  'glp-1': 'GLP-1',
  pause: 'Pause',
  peptide: 'Peptide Care',
  fibromyalgia: 'Fibromyalgia Care',
  menstrual: 'Menstrual Care',
  fertility: 'Fertility Care'
};

/** URL segment per programme, and the way back from one. */
export const PROGRAMME_PATHS = {
  'glp-1': 'glp-1',
  pause: 'pause',
  peptide: 'peptide',
  fibromyalgia: 'fibromyalgia',
  menstrual: 'menstrual',
  fertility: 'fertility'
};

export const PROGRAMME_BY_PATH = Object.entries(PROGRAMME_PATHS).reduce((map, [key, path]) => {
  map[path] = key;
  return map;
}, {});

/**
 * One hue per programme, fixed, so a programme keeps its colour on the section
 * overview and on its own page.
 *
 * NONE OF THESE CARRIES A VERDICT. There is no "good" programme and no "bad"
 * one — a person managing chronic pain and a person tracking a cycle are doing
 * two different things, not scoring differently at the same thing. Nothing in
 * this file ramps from red to green across programmes for that reason.
 */
export const PROGRAMME_COLORS = {
  'glp-1': CHART_COLORS.teal,
  pause: CHART_COLORS.brand,
  peptide: CHART_COLORS.blue,
  fibromyalgia: CHART_COLORS.orange,
  menstrual: CHART_COLORS.pink,
  fertility: CHART_COLORS.moss
};

export const programmeColor = (key) => PROGRAMME_COLORS[key] || CHART_COLORS.gray;

export const rampFor = (sectionKey) =>
  (SECTION_PROGRAMMES[sectionKey] || []).map((key) => programmeColor(key));

/**
 * How long somebody has been enrolled, regardless of how much they use it.
 * Reads as a scale because it is one — time, not merit.
 */
export const TENURE_RAMP = [
  CHART_COLORS.brandSoft,
  CHART_COLORS.blue,
  CHART_COLORS.brand,
  CHART_COLORS.moss,
  CHART_COLORS.greenDeep
];

/** Distinct days checked in over the last thirty — sparse to daily. */
export const ACTIVITY_RAMP = [
  CHART_COLORS.gray,
  CHART_COLORS.brandSoft,
  CHART_COLORS.blue,
  CHART_COLORS.brand,
  CHART_COLORS.greenDeep
];

/**
 * One programme, two, or three or more. Deliberately NOT a heat scale: being on
 * three of these is not a worse or better state to be in, it is simply a person
 * using more of what they were offered.
 */
export const BREADTH_RAMP = [CHART_COLORS.brandSoft, CHART_COLORS.brand, CHART_COLORS.moss];

/** Weekday bars. One hue — a Tuesday is not better than a Thursday. */
export const WEEKDAY_COLOR = CHART_COLORS.brand;
